'use client';

import type { FC } from 'react';
import {
  Alert,
  Button,
  Empty,
  Modal,
  Radio,
  Select,
  Space,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  formatConfidencePercent,
  normalizeHitlCandidates,
} from '~/app/services/kms/kms-document.utils';
import type { KmsHitlQueueItem } from '~/app/services/kms/kms-document.types';

export interface KmsHitlReviewModalProps {
  item: KmsHitlQueueItem | null;
  open: boolean;
  onClose: () => void;
  candidates: ReturnType<typeof normalizeHitlCandidates>;
  selectedCandidateKey?: string;
  onCandidateChange: (key: string) => void;
  resolvedDocumentType?: string;
  onResolvedDocumentTypeChange: (value: string | undefined) => void;
  documentTypeOptions: { value: string; label: string }[];
  overrideAssetId?: string;
  onOverrideAssetChange: (assetId: string | undefined) => void;
  assetOptions: { value: string; label: string }[];
  assetsLoading: boolean;
  canResolve: boolean;
  canMarkGlobal: boolean;
  isPlatformLibrary?: boolean;
  isOrgWideReview?: boolean;
  isAssetLinkReview?: boolean;
  onConfirm: () => void;
  onOverride: () => void;
  onMarkGlobal: () => void;
  isResolving: boolean;
}

export const KmsHitlReviewModal: FC<KmsHitlReviewModalProps> = ({
  item,
  open,
  onClose,
  candidates,
  selectedCandidateKey,
  onCandidateChange,
  resolvedDocumentType,
  onResolvedDocumentTypeChange,
  documentTypeOptions,
  overrideAssetId,
  onOverrideAssetChange,
  assetOptions,
  assetsLoading,
  canResolve,
  canMarkGlobal,
  isPlatformLibrary = false,
  isOrgWideReview = false,
  isAssetLinkReview = false,
  onConfirm,
  onOverride,
  onMarkGlobal,
  isResolving,
}) => {
  const { t } = useTranslation('kms');
  const markGlobalLabel = isPlatformLibrary
    ? t('hitl.actions.markPlatformGlobal')
    : t('hitl.actions.markOrgGlobal');
  const confirmLabel = isAssetLinkReview
    ? t('hitl.actions.confirmAsset')
    : isOrgWideReview
      ? t('hitl.actions.confirmOrgWide')
      : t('hitl.actions.confirm');

  return (
    <Modal
      title={
        isAssetLinkReview ? t('hitl.assetModalTitle') : t('hitl.modalTitle')
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      {!item ? null : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Typography.Title level={5}>{item.fileName}</Typography.Title>
            <Typography.Text type="secondary">
              {item.classifiedDocumentType
                ? t('hitl.typeLabel', { type: item.classifiedDocumentType })
                : t('hitl.typeUnknown')}
            </Typography.Text>
          </div>

          {isAssetLinkReview ? (
            <Alert type="info" showIcon message={t('hitl.assetLinkHint')} />
          ) : null}

          {isOrgWideReview && !isPlatformLibrary ? (
            <Alert type="info" showIcon message={t('hitl.orgWideHint')} />
          ) : null}

          <div>
            <Typography.Text strong>
              {isAssetLinkReview
                ? t('hitl.aiAssetSuggestions')
                : t('hitl.aiTypeSuggestions')}
            </Typography.Text>
            {candidates.length === 0 ? (
              <Empty
                description={
                  isAssetLinkReview
                    ? t('hitl.noAssetSuggestion')
                    : t('hitl.noSuggestion')
                }
                style={{ marginTop: 8 }}
              />
            ) : (
              <Radio.Group
                value={selectedCandidateKey}
                onChange={(event) => onCandidateChange(event.target.value)}
                style={{ width: '100%', marginTop: 8 }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {candidates.map((candidate) => (
                    <Radio key={candidate.key} value={candidate.key}>
                      <Space direction="vertical" size={0}>
                        <Typography.Text>
                          {isAssetLinkReview
                            ? candidate.label
                            : (candidate.documentType ?? candidate.label)}{' '}
                          — {formatConfidencePercent(candidate.score)}
                        </Typography.Text>
                        {candidate.reason ? (
                          <Typography.Text type="secondary">
                            {candidate.reason}
                          </Typography.Text>
                        ) : null}
                        {isAssetLinkReview && candidate.assetId ? (
                          <Typography.Text type="secondary" code>
                            {candidate.assetId}
                          </Typography.Text>
                        ) : null}
                      </Space>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            )}
          </div>

          {!isAssetLinkReview ? (
            <div>
              <Typography.Text strong>
                {t('hitl.resolvedTypeLabel')}
              </Typography.Text>
              <Select
                allowClear
                style={{ width: '100%', marginTop: 8 }}
                placeholder={t('hitl.resolvedTypePlaceholder')}
                options={documentTypeOptions}
                value={resolvedDocumentType}
                onChange={onResolvedDocumentTypeChange}
              />
            </div>
          ) : null}

          {!isPlatformLibrary && !isOrgWideReview && !isAssetLinkReview ? (
            <div>
              <Typography.Text strong>
                {t('hitl.overrideLabel')}
              </Typography.Text>
              <Typography.Paragraph
                type="secondary"
                style={{ marginBottom: 8 }}
              >
                {t('hitl.overrideHelp')}
              </Typography.Paragraph>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                loading={assetsLoading}
                style={{ width: '100%', marginTop: 8 }}
                placeholder={t('hitl.overridePlaceholder')}
                options={assetOptions}
                value={overrideAssetId}
                onChange={(value) => onOverrideAssetChange(value)}
              />
            </div>
          ) : null}

          {!canResolve ? (
            <Alert type="info" showIcon message={t('hitl.readOnly')} />
          ) : null}

          <Space wrap>
            <Button
              type="primary"
              disabled={!canResolve}
              loading={isResolving}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
            {!isPlatformLibrary && !isOrgWideReview && !isAssetLinkReview ? (
              <Button
                disabled={!canResolve}
                loading={isResolving}
                onClick={onOverride}
              >
                {t('hitl.actions.override')}
              </Button>
            ) : null}
            {canMarkGlobal && !isOrgWideReview ? (
              <Button loading={isResolving} onClick={onMarkGlobal}>
                {markGlobalLabel}
              </Button>
            ) : null}
          </Space>
        </Space>
      )}
    </Modal>
  );
};
